import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'
import invariant from 'tiny-invariant'
import { DeferredRevenueScheduleRecognitionMethod } from 'RevenueRecognition/revenueRecognition.types'

type UseDeferredRevenueSchedules = (props: { scheduleId: string }) =>
  | {
      summary: {
        id: string
        customer: {
          id: string
          name: string
          domain?: string
        }
        product: {
          id: string
          name: string
        }
        invoice: {
          id: string
          name: string
        }
        recognitionTerm?: {
          start: Date
          end: Date
        }
        method: DeferredRevenueScheduleRecognitionMethod
      }
      isLoading: false
    }
  | {
      summary: null
      isLoading: true
    }

/**
 * Data acquisition for the summary
 * @param props
 * @returns
 */
export const useDefRevScheduleSummary: UseDeferredRevenueSchedules = (props: {
  scheduleId: string
}) => {
  const scheduleQuery = useQuery(
    dashboardv99990101Client.getDeferredRevenueSchedule,
    { id: props.scheduleId },
    {
      select: res => {
        if (!res) {
          return null
        }

        return {
          id: res.id,
          customer: res.customer,
          product: res.product,
          invoice: res.invoice,
          method: res.recognitionMethod,
          recognitionTerm: res.recognitionTerm && {
            start: new Date(res.recognitionTerm.start),
            end: new Date(res.recognitionTerm.endInclusive)
          }
        }
      }
    }
  )

  /**
   * We throw this error to be caught by the appropriate error boundary.
   */
  if (scheduleQuery.error) {
    throw new Error('Schedule summary could not be loaded')
  }

  if (scheduleQuery.isPending) {
    return {
      isLoading: true,
      summary: null
    }
  }

  invariant(scheduleQuery.data, 'Summary should be defined, if loaded')

  return {
    isLoading: false,
    summary: scheduleQuery.data
  }
}
